import {
  Box,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import React, { useMemo } from 'react'
import { FeatureFlagName } from '../../../api/feature-flags-api'
import {
  formatCurrency,
  formatDate,
  formatPayoutWithDecimals,
  formatPercentage,
  formatWamYears,
  getMaxDecimalPlaces,
} from '../../shared/formats'
import { useFeatureFlags } from '../../shared/use-feature-flags'
import { PaymentRowType, Scenario, ScheduleType } from '../../shared/types'
import { convertScenarios } from '../create-proposal/conversion-utils'
import './scenario-cards.scss'

interface ScenarioCardsProps {
  scenarios: Scenario[]
  scenarioNamesMap: { [scenarioId: string]: string }
  viewScheduleType: ScheduleType
  originalScheduleType: ScheduleType
}

/**
 * Written by Windsurf using model: Claude Sonnet 4.5 (Thinking)
 */
export const ScenarioCards: React.FC<ScenarioCardsProps> = ({
  scenarios,
  scenarioNamesMap,
  viewScheduleType,
  originalScheduleType,
}) => {
  const { flags } = useFeatureFlags()
  const useDollarAmounts =
    flags[FeatureFlagName.ENABLE_PARTIAL_MATURITIES_BY_DOLLAR_AMOUNT] ?? false

  // Convert scenarios if view type differs from original
  const displayScenarios = useMemo(() => {
    if (viewScheduleType === originalScheduleType) {
      return scenarios
    }
    return convertScenarios(scenarios, viewScheduleType, useDollarAmounts)
  }, [scenarios, viewScheduleType, originalScheduleType, useDollarAmounts])
  return (
    <Box className="scenario-cards-container">
      {displayScenarios.map((scenario) => {
        // Written by Windsurf using model: Claude Sonnet 4.5 (Thinking)
        const payoutDecimals = getMaxDecimalPlaces(
          scenario.paymentSchedule.map((row) => row.payoutPercentage)
        )

        return (
          <Card key={scenario.id} className="scenario-card">
            <CardContent>
              <Typography
                variant="h6"
                gutterBottom
                className="scenario-card-title"
              >
                {scenarioNamesMap[scenario.id]}
              </Typography>
              <Grid container spacing={2} className="scenario-details-grid">
                <Grid size={{ xs: 6 }}>
                  <Typography
                    variant="subtitle2"
                    className="scenario-detail-label"
                  >
                    Proposed Rate
                  </Typography>
                  <Typography>
                    {formatPercentage(scenario.proposedRate)}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <Typography
                    variant="subtitle2"
                    className="scenario-detail-label"
                  >
                    WAM (years)
                  </Typography>
                  <Typography>{formatWamYears(scenario.wamYears)}</Typography>
                </Grid>
              </Grid>
              <Typography
                variant="subtitle2"
                className="scenario-payment-schedule-title"
              >
                Payment Schedule
              </Typography>
              <Table size="small" className="scenario-payment-table">
                <TableHead>
                  <TableRow>
                    <TableCell>Payout</TableCell>
                    <TableCell>Payment Date</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {scenario.paymentSchedule
                    .slice()
                    .sort((a, b) => {
                      if (a.paymentDate == null || b.paymentDate == null)
                        return 0
                      const dateA = new Date(a.paymentDate)
                      const dateB = new Date(b.paymentDate)
                      return dateA.getTime() - dateB.getTime()
                    })
                    .map((row, rowIndex) => {
                      const getPayoutDisplay = () => {
                        if (!useDollarAmounts) {
                          return formatPayoutWithDecimals(
                            row.payoutPercentage,
                            payoutDecimals
                          )
                        }

                        if (row.inputType === PaymentRowType.DOLLAR) {
                          return formatCurrency(row.payoutAmount)
                        }

                        return formatPayoutWithDecimals(
                          row.payoutAmount,
                          payoutDecimals
                        )
                      }

                      return (
                        <TableRow key={`${scenario.id}-${rowIndex}`}>
                          <TableCell>{getPayoutDisplay()}</TableCell>
                          <TableCell>
                            {formatDate(row.paymentDate?.toString())}
                          </TableCell>
                        </TableRow>
                      )
                    })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )
      })}
    </Box>
  )
}
